import { useMemo } from 'react';
import { Flame, Check, Calendar } from 'lucide-react';
import useLocalState from '../hooks/useLocalState.js';
import {
  computeStreak,
  recentDaysStatus,
  todayLocalDateString,
} from '../lib/streak.js';

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

export default function StreakTracker() {
  // checkIns: deduplicated array of YYYY-MM-DD strings (local time).
  // Schema versioned in case we ever attach metadata (e.g. amount logged
  // in a future PR) and need a migration.
  const [checkIns, setCheckIns] = useLocalState('streakCheckIns', [], 1);

  const today = todayLocalDateString();
  const streak = useMemo(() => computeStreak(checkIns, today), [checkIns, today]);
  const strip  = useMemo(() => recentDaysStatus(checkIns, 14, today), [checkIns, today]);

  function logToday() {
    if (streak.todayLogged) return;
    setCheckIns(prev => [...new Set([...prev, today])].sort());
  }

  return (
    <CardShell>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
            <Flame size={20} className="text-stone-900" />
          </div>
          <h3 className="font-display text-lg font-semibold text-cream">Daily Streak</h3>
        </div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-stone-500">
          {streak.todayLogged ? 'Locked in' : 'Not logged'}
        </div>
      </div>

      {/* Streak count + flame */}
      <div className="flex items-baseline gap-3 mb-4">
        <div className="font-mono tabular-nums text-5xl font-bold text-cream">
          {streak.length}
        </div>
        <div className="text-stone-400 text-sm">
          {streak.length === 1 ? 'day' : 'days'}
          {streak.length > 0 && <span className="text-amber-400 ml-1">🔥</span>}
        </div>
      </div>

      {/* 14-day strip */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-stone-500">Last 14 days</span>
          <span className="text-[10px] uppercase tracking-wider text-stone-600">
            Grace this week: {streak.graceRemainingThisWeek}/1
          </span>
        </div>
        <div className="flex gap-1">
          {strip.map(d => (
            <DayPip key={d.date} status={d.status} date={d.date} isToday={d.date === today} />
          ))}
        </div>
        <div className="flex gap-3 mt-2 text-[10px] text-stone-600">
          <Legend swatch="bg-emerald-400" label="Logged" />
          <Legend swatch="bg-amber-400/40" label="Grace" />
          <Legend swatch="bg-stone-700" label="Missed" />
        </div>
      </div>

      {/* Action button */}
      <button
        onClick={logToday}
        disabled={streak.todayLogged}
        className={`w-full rounded-lg py-3 px-4 font-semibold text-sm transition flex items-center justify-center gap-2 ${
          streak.todayLogged
            ? 'bg-emerald-500/10 text-emerald-400 cursor-default border border-emerald-500/20'
            : 'bg-gradient-to-br from-orange-500 to-amber-400 text-stone-900 hover:brightness-110'
        }`}
      >
        {streak.todayLogged ? (
          <>
            <Check size={16} />
            <span>Today's save logged</span>
          </>
        ) : (
          <>
            <Flame size={16} />
            <span>I stacked sats today</span>
          </>
        )}
      </button>

      <p className="text-[11px] text-stone-600 mt-3 leading-relaxed">
        One tap a day keeps the streak alive. Miss a day, you have 1 grace skip per week
        before the streak resets. How many sats you stack is up to you — we trust the habit.
      </p>
    </CardShell>
  );
}

function DayPip({ status, date, isToday }) {
  const cls = {
    logged:  'bg-emerald-400',
    grace:   'bg-amber-400/40 border border-amber-400/60',
    missed:  'bg-stone-700',
    future:  'bg-stone-800',
  }[status] || 'bg-stone-800';
  const ring = isToday ? 'ring-2 ring-amber-400 ring-offset-1 ring-offset-stone-900' : '';
  return (
    <div
      className={`flex-1 h-6 rounded-sm ${cls} ${ring}`}
      title={`${date}${isToday ? ' (today)' : ''} — ${status}`}
    />
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`inline-block w-2 h-2 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
}
